import { createContext, Dispatch, SetStateAction, useEffect, useState } from "react";
import { ButtonProps } from "@mui/material";
import { Editor } from "@craftjs/core";
import lz from "lzutf8";
import GoogleFontLoader from "react-google-font-loader";
import SectionBlock from "@/components/editor/blocks/section-block/SectionBlock";
import ContainerBlock from "@/components/editor/blocks/ContainerBlock";
import FormBlock from "@/components/editor/blocks/FormBlock";
import TextInputBlock from "@/components/editor/blocks/TextInputBlock";
import TextBlock from "@/components/editor/blocks/TextBlock";
import ImageBlock from "@/components/editor/blocks/ImageBlock";
import ButtonBlock from "@/components/editor/blocks/ButtonBlock";
import VerticalSpacerBlock from "@/components/editor/blocks/VerticalSpacerBlock";
import DishBlock from "@/components/editor/blocks/DishBlock";
import DropRegionBlock from "@/components/editor/blocks/DropRegionBlock";
import NumberInputBlock from "@/components/editor/blocks/NumberInputBlock";
import DateInputBlock from "@/components/editor/blocks/DateInputBlock";
import RenderNode from "./components/RenderNode/RenderNode";
import ViewPort from "./components/ViewPort/ViewPort";
import SocialMediaBlock from "@/components/editor/blocks/SocialMediaBlock";
import fonts from "@/constants/fonts";
import MapBlock from "./blocks/MapBlock";
import FaqsBlock from "./blocks/FaqsBlock";
import MenuBlock from "./blocks/MenuBlock";
import CartBlock from "./blocks/CartBlock";
import RegionBlock from "./blocks/RegionBlock";
import RepeatBlock from "./blocks/RepeatBlock";
import { Menu, Template, Website, WebsiteElement } from "@/types";
import CarouselBlock from "./blocks/CarouselBlock";
import { useParams } from "react-router-dom";

export interface Props {
  title: string;
  data: string;
  actions?: ButtonProps[];
  onSave: (data: string) => void;
  onClose: () => void;
  menu?: Menu;
  website?: Website;
  template?: Template;
  elements: WebsiteElement[] | null;
}

export type ViewPort = "sm" | "md" | "lg";

interface EditorContext {
  menu: Menu | null;
  website: Website | null;
  template: Template | null;
  elements: WebsiteElement[] | null;
  styles: string;
  isLoading: boolean;
  viewPort: ViewPort;
  setViewPort: Dispatch<SetStateAction<ViewPort>>;
  setStyles: Dispatch<SetStateAction<string>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const EditorContext = createContext<EditorContext>({
  menu: null,
  template: null,
  website: null,
  elements: null,
  styles: "",
  isLoading: false,
  viewPort: "sm",
  setStyles: () => undefined,
  setViewPort: () => undefined,
  setIsLoading: () => undefined,
});

const MainEditor = (props: Props) => {
  const [viewPort, setViewPort] = useState<ViewPort>("sm");
  const [isLoading, setIsLoading] = useState(false);
  const [json, setJson] = useState<string | null>(null);
  const { elementId } = useParams<{ elementId?: string }>();
  const [styles, setStyles] = useState(props.website?.styles ?? props?.menu?.styles ?? props?.template?.styles ?? "");

  useEffect(() => {
    if (props.data !== undefined) {
      const stateToLoad = lz.decompress(lz.decodeBase64(props.data));
      setJson(stateToLoad);
      // console.log("🚀 ~ stateToLoad:", stateToLoad);
    }

    // if (elementId) {
    //   const stateToLoad = lz.decompress(lz.decodeBase64(props.data));
    //   setJson(stateToLoad);
    // }
  }, [props?.data, props?.website?.styles]);

  if (json === null) return null;

  return (
    <EditorContext.Provider
      value={{
        menu: props?.menu ?? null,
        template: props?.template ?? null,
        website: props.website ?? null,
        elements: props?.elements ?? null,
        styles,
        viewPort,
        isLoading,
        setStyles,
        setViewPort,
        setIsLoading,
      }}
    >
      <GoogleFontLoader fonts={fonts} />
      <Editor
        enabled={true}
        resolver={{
          FormBlock,
          TextInputBlock,
          NumberInputBlock,
          DateInputBlock,
          SectionBlock,
          ContainerBlock,
          TextBlock,
          ImageBlock,
          CarouselBlock,
          VerticalSpacerBlock,
          DishBlock,
          DropRegionBlock,
          ButtonBlock,
          SocialMediaBlock,
          MapBlock,
          FaqsBlock,
          MenuBlock,
          CartBlock,
          RegionBlock,
          RepeatBlock,
        }}
        onRender={RenderNode}
      >
        <ViewPort {...props} styles={styles} json={json} />
      </Editor>
    </EditorContext.Provider>
  );
};

export default MainEditor;
